let _ = t => t,
  _t;
import { memo, useContext, useRef } from 'react';
import { MediaPlaybackTimeContext } from '../../context/MediaPlaybackTimeContext';
import { MediaPlaybackControlsContext } from '../../context/MediaPlaybackControlsContext';
import { LEFT_COL_WIDTH, RIGHT_COL_WIDTH } from '../../constants/MediaTimelinePositioning';
import { AbstractSlider } from 'foundations-components/transitional/components/AbstractSlider';
import styledWithTheme from 'foundations-theming/utils';
import { useControlsStatus } from '../../hooks/_internal/useShouldDisableControls';
import { jsx as _jsx } from "react/jsx-runtime";
const StyledSliderContainer = styledWithTheme.div(_t || (_t = _`
  position: absolute;
  height: 100%;
  left: ${0};
  right: ${0};
  top: 0;
  cursor: ${0};
  user-select: none;
`), LEFT_COL_WIDTH, RIGHT_COL_WIDTH, ({
  isError
}) => isError ? 'not-allowed' : 'pointer');
const MediaTimelineOverlay = /*#__PURE__*/memo(props => {
  const {
    timelineRef,
    children,
    onDragCallback
  } = props;
  const currentTime = useContext(MediaPlaybackTimeContext);
  const {
    clipStartTime,
    onPlayerHeadUpdate,
    pause,
    play,
    preciseDuration
  } = useContext(MediaPlaybackControlsContext);
  const {
    isPaused,
    isError,
    isBuffering
  } = useControlsStatus();
  const dragWhilePlaying = useRef(false);
  const controlPosition = currentTime / preciseDuration * 100;
  const proportionalTime = x => {
    if (timelineRef.current) {
      const rect = timelineRef.current.getBoundingClientRect();
      let newTime = (x - rect.x) / rect.width * preciseDuration;
      newTime = Math.min(newTime, preciseDuration);
      newTime = Math.max(newTime, 0);
      return newTime;
    }
    return clipStartTime;
  };
  const handleMouseMove = evt => {
    evt.stopPropagation();
    const newTime = proportionalTime(evt.clientX);
    onPlayerHeadUpdate(newTime);
  };
  const handleDrag = evt => {
    // Trellis types only the target, not the event, so we need to cast to the SyntheticEvent type
    evt === null || evt === void 0 || evt.stopPropagation();
    const value = evt.target.value;
    const timeToSkipTo = Math.min(value.percentX * preciseDuration, preciseDuration);
    onPlayerHeadUpdate(timeToSkipTo);
  };
  const handleDragStart = () => {
    if (!isPaused) {
      pause();
      dragWhilePlaying.current = true;
    }
  };
  const handleDragEnd = () => {
    if (onDragCallback) {
      onDragCallback();
    }
    if (dragWhilePlaying.current) {
      play();
      dragWhilePlaying.current = false;
    }
  };
  const handleMouseUp = evt => {
    evt.stopPropagation();
    handleDragEnd();
    const newTime = proportionalTime(evt.clientX);
    onPlayerHeadUpdate(newTime);
    window.removeEventListener('mouseup', handleMouseUp);
    window.removeEventListener('mousemove', handleMouseMove);
  };
  const handleMouseDown = evt => {
    if (isError || isBuffering) return;
    evt.stopPropagation();
    handleDragStart();
    window.addEventListener('mouseup', handleMouseUp, false);
    window.addEventListener('mousemove', handleMouseMove, false);
  };
  return /*#__PURE__*/_jsx(StyledSliderContainer, {
    "data-test-id": "mediaTimeline-Overlay",
    onMouseDown: handleMouseDown,
    isError: isError,
    children: /*#__PURE__*/_jsx(AbstractSlider, {
      "aria-valuemax": 100,
      "aria-valuemin": 0,
      "aria-valuenow": controlPosition || 0,
      "aria-valuetext": `${'test' || 0}`,
      role: "slider",
      tabIndex: 0,
      onDrag: handleDrag,
      onDragStart: handleDragStart,
      onDragEnd: handleDragEnd,
      children: children
    })
  });
});
export default /*#__PURE__*/memo(MediaTimelineOverlay);